// Information tab (Phase 1).
// Free, educational content. Reads from public.info_articles via the
// authenticated client (RLS allows SELECT where published = true).
//
// Visibility of the tab itself is gated to profiles.is_test_user at the
// nav site (KnowMenoApp.tsx). This component does not re-enforce that.
//
// Styling: matches the rest of KnowMenoApp — same inline hex palette and
// `system-ui, sans-serif` (inherited from the root container). No bespoke
// fonts or colors. See KnowMenoApp.tsx:57–62 for the source palette.

import { useEffect, useMemo, useState } from "react";
import {
  Flame, Moon, BatteryLow, Heart, Brain, Activity, HeartPulse, BookOpen,
  ShieldCheck, ExternalLink,
  type LucideIcon,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import brainFogAsset from "@/assets/brain-fog.png.asset.json";
import fatigueAsset from "@/assets/fatigue.png.asset.json";
import hotFlashesAsset from "@/assets/hot-flashes.png.asset.json";
import jointPainAsset from "@/assets/joint-pain.png.asset.json";
import moodAnxietyAsset from "@/assets/mood-anxiety.png.asset.json";
import postMenopauseAsset from "@/assets/post-menopause.png.asset.json";
import sleepAsset from "@/assets/sleep.png.asset.json";
import trustedSourcesAsset from "@/assets/trusted-sources.png.asset.json";

// Palette — mirrors the constants in KnowMenoApp.tsx (single visual system).
const PLUM = "#4B1F4F";       // TD / PD / AD
const PLUM_HOVER = "#3A1840"; // existing hover used across the app
const CORAL = "#FF5C7A";      // TM / PU / RO
const ROSE = "#6E4B71";       // GR — muted secondary text
const TEXT = "#6E4B71";       // GD — body text
const SURFACE = "#FFFFFF";    // WH
const BG_SOFT = "#FAF7F5";    // GL / PL — ivory
const BORDER = "#E8D5DA";

// Human labels for topic_key. Unknown keys fall back to title-cased key.
const TOPIC_LABELS: Record<string, string> = {
  hot_flashes: "Hot Flashes",
  night_sweats: "Night Sweats",
  sleep: "Sleep",
  mood: "Mood & Anxiety",
  brain_fog: "Brain Fog",
  joint_pain: "Joint Pain",
  weight: "Weight Changes",
  libido: "Libido & Sexual Health",
  vaginal_health: "Vaginal Health",
  bone_health: "Bone Health",
  heart_health: "Heart Health",
  hrt: "Hormone Therapy",
  postmeno_care: "Post-Menopause Care",
};

// Per-topic visual config. `imageUrl` is optional — when set, it replaces the
// gradient + icon header band. All gradient colors are drawn from the existing
// inline palette above (no new hex values introduced).
type TopicVisual = {
  icon: LucideIcon;
  gradient: [string, string]; // from → to (existing palette only)
  imageUrl?: string;
};
const DEFAULT_VISUAL: TopicVisual = {
  icon: BookOpen,
  gradient: [BG_SOFT, BORDER],
};
const TOPIC_VISUALS: Record<string, TopicVisual> = {
  hot_flashes:   { icon: Flame,       gradient: [CORAL, PLUM], imageUrl: hotFlashesAsset.url },
  night_sweats:  { icon: Moon,        gradient: [ROSE, PLUM] },
  sleep:         { icon: Moon,        gradient: [ROSE, PLUM], imageUrl: sleepAsset.url },
  fatigue:       { icon: BatteryLow,  gradient: [BORDER, ROSE], imageUrl: fatigueAsset.url },
  mood:          { icon: Heart,       gradient: [CORAL, ROSE], imageUrl: moodAnxietyAsset.url },
  brain_fog:     { icon: Brain,       gradient: [BG_SOFT, ROSE], imageUrl: brainFogAsset.url },
  joint_pain:    { icon: Activity,    gradient: [ROSE, CORAL], imageUrl: jointPainAsset.url },
  postmeno_care: { icon: HeartPulse,  gradient: [PLUM, CORAL], imageUrl: postMenopauseAsset.url },
};
function topicVisual(key: string): TopicVisual {
  return TOPIC_VISUALS[key] || DEFAULT_VISUAL;
}

function topicLabel(key: string): string {
  return (
    TOPIC_LABELS[key] ||
    key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
  );
}

// Renders the topic header band: image if `imageUrl` is set, otherwise a soft
// gradient with the topic's icon. `size` controls the band height.
function TopicHeader({ topicKey, size }: { topicKey: string; size: "card" | "article" }) {
  const v = topicVisual(topicKey);
  const height = size === "card" ? 64 : 44;
  const iconSize = size === "card" ? 26 : 20;
  if (v.imageUrl) {
    return (
      <img
        src={v.imageUrl}
        alt=""
        loading="lazy"
        style={{
          display: "block", width: "100%",
          objectFit: "cover",
          objectPosition: size === "article" ? "center 30%" : "center",
          aspectRatio: size === "card" ? "3 / 4" : "16 / 9",
        }}
      />
    );
  }
  const Icon = v.icon;
  return (
    <div
      aria-hidden
      style={{
        width: "100%", height,
        background: `linear-gradient(135deg, ${v.gradient[0]} 0%, ${v.gradient[1]} 100%)`,
        display: "flex", alignItems: "center", justifyContent: "flex-start",
        padding: size === "card" ? "0 14px" : "0 12px",
      }}
    >
      <Icon size={iconSize} color={SURFACE} strokeWidth={2} />
    </div>
  );
}

// Read-time: 200 wpm, rounded up, minimum 1 minute. Strips markdown emphasis.
function readMinutes(body: string): number {
  const words = body
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

type ExternalLink = {
  id: string;
  topic_key: string;
  title: string;
  source_name: string;
  url: string;
  summary: string | null;
  locale: string;
  sort_order: number;
};

type Article = {
  id: string;
  slug: string;
  topic_key: string;
  article_type: "why_it_happens" | "what_helps" | "overview";
  locale: string;
  title: string;
  body_markdown: string;
  sources: string | null;
  sort_order: number;
};

type Topic = {
  topic_key: string;
  label: string;
  articles: Article[];
  totalMinutes: number;
};

// --- Minimal markdown renderer ---------------------------------------------
// Supports: paragraphs (blank-line separated), **bold**, *italic*.
// No links/lists/headings — none appear in the seeded copy. Safe by default
// because we render text nodes only (no dangerouslySetInnerHTML).
function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={`b${i}`} style={{ fontWeight: 600 }}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<em key={`i${i}`} style={{ fontStyle: "italic" }}>{m[2]}</em>);
    last = re.lastIndex;
    i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Markdown({ body }: { body: string }) {
  const paragraphs = body.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  return (
    <div style={{ fontSize: 15, lineHeight: 1.65, color: TEXT }}>
      {paragraphs.map((p, idx) => (
        <p key={idx} style={{ margin: "0 0 14px 0" }}>
          {renderInline(p)}
        </p>
      ))}
    </div>
  );
}

// (Per-topic Trusted Sources section removed — links now live in a single
// dedicated card at the end of the topic grid.)


// --- Component -------------------------------------------------------------

export default function InfoTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [externalLinks, setExternalLinks] = useState<ExternalLink[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [showSources, setShowSources] = useState(false);


  const userLocale = useMemo(() => {
    try {
      const lang = (typeof navigator !== "undefined" && navigator.language) || "en";
      return lang.split("-")[0].toLowerCase();
    } catch { return "en"; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const localesFilter = userLocale === "en" ? ["en"] : [userLocale, "en"];
        const [articlesRes, linksRes] = await Promise.all([
          supabase
            .from("info_articles")
            .select("id, slug, topic_key, article_type, locale, title, body_markdown, sources, sort_order")
            .in("locale", localesFilter)
            .order("sort_order", { ascending: true }),
          supabase
            .from("info_external_links")
            .select("id, topic_key, title, source_name, url, summary, locale, sort_order")
            .in("locale", localesFilter)
            .order("sort_order", { ascending: true }),
        ]);
        if (cancelled) return;
        if (articlesRes.error) { setError(articlesRes.error.message || "Failed to load articles"); setLoading(false); return; }
        setArticles((articlesRes.data || []) as Article[]);
        if (!linksRes.error) setExternalLinks((linksRes.data || []) as ExternalLink[]);
        setLoading(false);
      } catch (e: any) {
        if (!cancelled) { setError(e?.message || "Failed to load articles"); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [userLocale]);

  // All trusted sources for the user's locale (fallback 'en'), deduped by url
  // (keep first occurrence), grouped by source_name, sorted by sort_order.
  const sourceGroups = useMemo(() => {
    const preferred = externalLinks.filter(l => l.locale === userLocale);
    const fallback = userLocale === "en" ? [] : externalLinks.filter(l => l.locale === "en");
    const seenUrls = new Set<string>();
    const deduped: ExternalLink[] = [];
    for (const l of [...preferred, ...fallback]) {
      if (seenUrls.has(l.url)) continue;
      seenUrls.add(l.url);
      deduped.push(l);
    }
    const groups = new Map<string, ExternalLink[]>();
    for (const l of deduped) {
      const arr = groups.get(l.source_name) || [];
      arr.push(l);
      groups.set(l.source_name, arr);
    }
    const out: { source_name: string; links: ExternalLink[] }[] = [];
    for (const [source_name, arr] of groups) {
      arr.sort((a, b) => a.sort_order - b.sort_order);
      out.push({ source_name, links: arr });
    }
    out.sort((a, b) => a.source_name.localeCompare(b.source_name));
    return { groups: out, totalLinks: deduped.length, totalSources: groups.size };
  }, [externalLinks, userLocale]);



  const topics: Topic[] = useMemo(() => {
    const byTopic = new Map<string, Article[]>();
    for (const a of articles) {
      const list = byTopic.get(a.topic_key) || [];
      list.push(a);
      byTopic.set(a.topic_key, list);
    }
    const out: Topic[] = [];
    for (const [key, list] of byTopic) {
      let chosen = list.filter(a => a.locale === userLocale);
      if (chosen.length === 0) chosen = list.filter(a => a.locale === "en");
      if (chosen.length === 0) continue;
      chosen.sort((a, b) => a.sort_order - b.sort_order);
      const totalMinutes = chosen.reduce((s, a) => s + readMinutes(a.body_markdown), 0);
      out.push({ topic_key: key, label: topicLabel(key), articles: chosen, totalMinutes });
    }
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [articles, userLocale]);

  const activeArticle = useMemo(() => {
    if (!selectedArticleId) return null;
    return articles.find(a => a.id === selectedArticleId) || null;
  }, [articles, selectedArticleId]);

  const activeTopicObj = useMemo(() => {
    if (!selectedTopic) return null;
    return topics.find(t => t.topic_key === selectedTopic) || null;
  }, [topics, selectedTopic]);

  // -------- Render states --------

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: "center", color: ROSE, fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 16, color: CORAL, fontSize: 13 }}>
        Couldn't load articles: {error}
      </div>
    );
  }

  // Article view
  if (activeArticle) {
    const isPostmenoCare = activeArticle.topic_key === "postmeno_care";
    const ctaLabel = isPostmenoCare ? "See your Vitality Report" : "Track this symptom";
    const onCta = () => {
      if (typeof window === "undefined") return;
      if (isPostmenoCare) {
        // Vitality Report lives inside the Care tab.
        window.dispatchEvent(new CustomEvent("knowmeno-set-tab", { detail: "care" }));
      } else {
        // Jump to the Today tab where daily logging lives.
        window.dispatchEvent(new CustomEvent("knowmeno-set-tab", { detail: "today" }));
      }
    };
    const mins = readMinutes(activeArticle.body_markdown);
    return (
      <div style={{ padding: "4px 4px 24px" }}>
        <button
          onClick={() => setSelectedArticleId(null)}
          style={{
            background: "none", border: "none", color: ROSE, fontSize: 13,
            padding: "4px 0 12px", cursor: "pointer", fontFamily: "inherit",
          }}
        >
          ← Back
        </button>
        <div style={{
          borderRadius: 12, overflow: "hidden",
          border: `1px solid ${BORDER}`, marginBottom: 12,
        }}>
          <TopicHeader topicKey={activeArticle.topic_key} size="article" />
        </div>
        <h2 style={{
          fontSize: 22, lineHeight: 1.3, color: PLUM,
          margin: "0 0 6px 0", fontWeight: 600, letterSpacing: "-0.01em",
        }}>
          {activeArticle.title}
        </h2>
        <div style={{ fontSize: 12, color: ROSE, margin: "0 0 16px 0" }}>
          {mins} min read
        </div>
        <Markdown body={activeArticle.body_markdown} />

        <button
          onClick={onCta}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = PLUM_HOVER; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = PLUM; }}
          style={{
            marginTop: 8, width: "100%", padding: 14, borderRadius: 14,
            background: PLUM, color: SURFACE, border: "none",
            fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          {ctaLabel}
        </button>

        {activeArticle.sources && (
          <div style={{
            marginTop: 18, fontSize: 11, color: ROSE, lineHeight: 1.55,
            fontStyle: "italic",
          }}>
            Sources: {activeArticle.sources}
          </div>
        )}

        <div style={{
          marginTop: 16, padding: "10px 12px", background: BG_SOFT,
          borderRadius: 10, fontSize: 11, color: TEXT, lineHeight: 1.55,
        }}>
          This content is educational and is not medical advice. Always talk
          with your clinician about your personal situation.
        </div>
      </div>
    );
  }

  // Topic detail (article list) — only shown when topic has 2+ articles.
  if (activeTopicObj && activeTopicObj.articles.length > 1) {
    return (
      <div style={{ padding: "4px 4px 24px" }}>
        <button
          onClick={() => setSelectedTopic(null)}
          style={{
            background: "none", border: "none", color: ROSE, fontSize: 13,
            padding: "4px 0 12px", cursor: "pointer", fontFamily: "inherit",
          }}
        >
          ← Back
        </button>
        <h2 style={{
          fontSize: 20, color: PLUM,
          margin: "0 0 14px 0", fontWeight: 600,
        }}>
          {activeTopicObj.label}
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {activeTopicObj.articles.map(a => {
            const mins = readMinutes(a.body_markdown);
            return (
              <button
                key={a.id}
                onClick={() => setSelectedArticleId(a.id)}
                style={{
                  textAlign: "left", padding: "14px 16px", background: SURFACE,
                  border: `1px solid ${BORDER}`, borderRadius: 14, cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex", flexDirection: "column", gap: 4,
                }}
              >
                <div style={{ fontSize: 15, color: PLUM, fontWeight: 500 }}>{a.title}</div>
                <div style={{ fontSize: 11, color: ROSE }}>{mins} min read</div>
              </button>
            );
          })}
        </div>
        
      </div>
    );
  }

  // Trusted Sources list view — all links, deduped by url, grouped by source.
  if (showSources) {
    return (
      <div style={{ padding: "4px 4px 24px" }}>
        <button
          onClick={() => setShowSources(false)}
          style={{
            background: "none", border: "none", color: ROSE, fontSize: 13,
            padding: "4px 0 12px", cursor: "pointer", fontFamily: "inherit",
          }}
        >
          ← Back
        </button>
        <h2 style={{
          fontSize: 20, color: PLUM,
          margin: "0 0 6px 0", fontWeight: 600,
        }}>
          Trusted Sources
        </h2>
        <p style={{ margin: "0 0 18px 0", fontSize: 13, color: ROSE, lineHeight: 1.55 }}>
          Vetted reads from leading institutions.
        </p>
        {sourceGroups.groups.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: ROSE, fontSize: 13 }}>
            No sources available yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {sourceGroups.groups.map(g => (
              <div key={g.source_name}>
                <h3 style={{
                  fontSize: 12, color: ROSE, fontWeight: 600, margin: "0 0 8px 0",
                  textTransform: "uppercase", letterSpacing: "0.06em",
                }}>
                  {g.source_name}
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {g.links.map(l => (
                    <a
                      key={l.id}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer external"
                      style={{
                        display: "flex", alignItems: "flex-start", gap: 10,
                        padding: "12px 14px", background: SURFACE,
                        border: `1px solid ${BORDER}`, borderRadius: 12,
                        textDecoration: "none", fontFamily: "inherit",
                      }}
                    >
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ fontSize: 14, color: PLUM, fontWeight: 500, lineHeight: 1.35 }}>
                          {l.title}
                        </div>
                        {l.summary && (
                          <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.5 }}>{l.summary}</div>
                        )}
                      </div>
                      <ExternalLink size={14} color={ROSE} style={{ marginTop: 2, flexShrink: 0 }} />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Landing view: topic card grid.
  if (topics.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: "center", color: ROSE, fontSize: 13 }}>
        No articles available yet — check back soon.
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 4px 24px" }}>
      <h2 style={{
        fontSize: 20, color: PLUM,
        margin: "0 0 6px 0", fontWeight: 600,
      }}>
        Information
      </h2>
      <p style={{ margin: "0 0 18px 0", fontSize: 13, color: ROSE, lineHeight: 1.55 }}>
        Plain-language explainers on what you might be experiencing.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {topics.map(t => {
          const count = t.articles.length;
          const mins = t.totalMinutes;
          const onClick = () => {
            if (count === 1) setSelectedArticleId(t.articles[0].id);
            else setSelectedTopic(t.topic_key);
          };
          return (
            <button
              key={t.topic_key}
              onClick={onClick}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = PLUM; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = BORDER; }}
              style={{
                textAlign: "left", padding: 0,
                background: SURFACE, border: `1px solid ${BORDER}`,
                borderRadius: 14, cursor: "pointer", overflow: "hidden",
                display: "flex", flexDirection: "column", minHeight: 82,
                fontFamily: "inherit",
              }}
            >
              <TopicHeader topicKey={t.topic_key} size="card" />
              <div style={{
                padding: "10px 14px 12px",
                display: "flex", flexDirection: "column", gap: 4,
              }}>
                <div style={{
                  fontSize: 15, lineHeight: 1.3,
                  color: PLUM, fontWeight: 500,
                }}>
                  {t.label}
                </div>
                <div style={{ fontSize: 11, color: ROSE }}>
                  {count} {count === 1 ? "article" : "articles"} · {mins} min read
                </div>
              </div>
            </button>
          );
        })}

        {sourceGroups.totalLinks > 0 && (
          <button
            key="__trusted_sources__"
            onClick={() => setShowSources(true)}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = PLUM; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = BORDER; }}
            style={{
              textAlign: "left", padding: 0,
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 14, cursor: "pointer", overflow: "hidden",
              display: "flex", flexDirection: "column", minHeight: 82,
              fontFamily: "inherit",
            }}
          >
            <img
              src={trustedSourcesAsset.url}
              alt=""
              loading="lazy"
              style={{
                display: "block", width: "100%", objectFit: "cover",
                objectPosition: "center",
                aspectRatio: "3 / 4",
              }}
            />
            <div style={{
              padding: "10px 14px 12px",
              display: "flex", flexDirection: "column", gap: 4,
            }}>
              <div style={{
                fontSize: 15, lineHeight: 1.3,
                color: PLUM, fontWeight: 500,
              }}>
                Trusted Sources
              </div>
              <div style={{ fontSize: 11, color: ROSE, lineHeight: 1.45 }}>
                Vetted reads from leading institutions · {sourceGroups.totalSources} {sourceGroups.totalSources === 1 ? "source" : "sources"}
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

